import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpContextToken, HttpErrorResponse } from '@angular/common/http';
import { inject, Injector } from '@angular/core';

import { switchMap, catchError, throwError, Observable, from, of, map } from 'rxjs';
import { AlertNotificationsService } from '../../services/alert-notifications.service';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../auth/auth.service';
import { ProfileService } from '../auth/services/profile.service';
import { parsePermissionRequired, permissionDeniedMessage } from '../utils/partner-permissions.util';
import { isComponentHandledError } from '../utils/clinic-phone-errors.util';

// Marca una richiesta già ritentata dopo un refresh, per evitare loop su 401 ripetuti.
const RETRIED = new HttpContextToken<boolean>(() => false);

const authExcludedEndpoints = [
  'v1/auth',
  'v1/auth/login',
  'v1/auth/token',
  'v1/auth/signup',
  'v1/geo/',
  'v1/public/',
  'v1/partners/',
  'v1/languages/',
  'v1/languages',
  'v1/lead/',
  'v1/review/',
  'v1/requests',
  'v1/requests/verify-identity',
  'v1/request',
  'v1/account/verify',
  'v1/account/invitation',
  'v1/account/reset-password',
];

const skipErrorNotificationEndpoints = [
  'v1/requests',
  // Un refresh fallito non è mai un errore da mostrare all'utente: la sessione è
  // semplicemente scaduta e la gestisce forceLogout (redirect solo in area protetta).
  'v1/auth/refresh-token',
];

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const notificationService = inject(AlertNotificationsService);
  const translateService = inject(TranslateService);
  const injector = inject(Injector);
  const currentLang = translateService.currentLang || 'en';

  // Asset statici (traduzioni /assets/i18n/*.json, icone…): nessuna logica di token/lingua/toast.
  if (isStaticAsset(req.url)) {
    return next(req);
  }

  // Endpoint pubblici: nessuna gestione token, solo lingua.
  if (isExcludedEndpoint(req.url)) {
    const langReq = req.clone({ headers: req.headers.set('Accept-Language', currentLang) });
    return next(langReq).pipe(
      catchError(error => {
        maybeNotify(error, langReq, notificationService);
        return throwError(() => error);
      })
    );
  }

  // Refresh PROATTIVO on-demand: se il token è scaduto o sta per scadere e abbiamo
  // un refresh token, lo rinnoviamo PRIMA di inviare la richiesta. Nessun timer:
  // il controllo avviene a ogni chiamata, quindi è immune a reload/sleep del tab.
  if (authService.isUserLoggedIn() && authService.isTokenNearExpiry()) {
    if (authService.isRefreshTokenValid()) {
      return authService.refreshToken().pipe(
        switchMap(token => sendAuthed(req, next, authService, notificationService, currentLang, token, injector)),
        catchError(() => {
          forceLogout(authService);
          return throwError(() => new Error('Token refresh failed'));
        })
      );
    }

    // Refresh token assente o scaduto: la sessione non è recuperabile, inutile chiamare
    // /refresh-token (darebbe solo 401). Se l'access token è ancora valido (solo "in scadenza")
    // lo usiamo finché dura; se è già scaduto chiudiamo la sessione: in area protetta logout +
    // redirect come prima, fuori pulizia silenziosa e la richiesta parte senza Authorization.
    if (authService.isTokenExpired()) {
      forceLogout(authService);
      if (isOnProtectedArea()) {
        return throwError(() => new Error('Session expired'));
      }
      return sendAuthed(req, next, authService, notificationService, currentLang, null, injector);
    }
  }

  return sendAuthed(req, next, authService, notificationService, currentLang, authService.getToken(), injector);
};

function sendAuthed(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  notificationService: AlertNotificationsService,
  currentLang: string,
  token: string | null,
  injector: Injector
): Observable<any> {
  let headers = req.headers.set('Accept-Language', currentLang);
  if (token) {
    headers = headers.set('Authorization', `Bearer ${token}`);
  }
  const authReq = req.clone({ headers });

  return next(authReq).pipe(
    catchError(error => normalizeBlobError$(error).pipe(
      switchMap(normalized => handleAuthError(normalized, req, next, authService, notificationService, currentLang, injector))
    ))
  );
}

/**
 * I2 (blob 403s): a request made with `responseType: 'blob'` (e.g. downloadInvoicePdf) still gets
 * its error body back as a Blob even when the backend actually sent JSON (a PERMISSION_REQUIRED or
 * PARTNER_NOT_APPROVED 403) - Angular doesn't parse it for you just because the status is an
 * error. Without this, `error.error` is a Blob, parsePermissionRequired/isPartnerNotApprovedError
 * (which expect `error.error.message`) never match, and the request falls through to the generic
 * 403 handling (toast + logout) instead of being recognised as a permission/approval denial.
 * Parses the blob's text as JSON and rebuilds the HttpErrorResponse with that as its body; any
 * other error (not a 403, not a Blob, not JSON-typed, or not valid JSON once read) passes through
 * unchanged.
 */
export function normalizeBlobError$(error: any): Observable<any> {
  const isJsonBlob =
    error instanceof HttpErrorResponse &&
    error.status === 403 &&
    error.error instanceof Blob &&
    (error.error.type || '').includes('json');

  if (!isJsonBlob) {
    return of(error);
  }

  return from((error.error as Blob).text()).pipe(
    map(text => {
      try {
        const parsedBody = JSON.parse(text);
        return new HttpErrorResponse({
          error: parsedBody,
          headers: error.headers,
          status: error.status,
          statusText: error.statusText,
          url: error.url ?? undefined,
        });
      } catch {
        return error;
      }
    }),
    catchError(() => of(error)),
  );
}

// M1 (double toast): the same HttpErrorResponse instance is rethrown unchanged after the
// interceptor's own toast, so tagging it here survives to the component's catch block. Components
// that show their own error toast on a permission-gated action (accept/reject/unlock/label/quote
// create-edit/members/billing...) check isHandledPermissionError() first and skip theirs, so the
// user sees one toast per failure, not two.
function markPermissionHandled(error: any): void {
  if (error && typeof error === 'object') {
    error.permissionHandled = true;
  }
}

export function isHandledPermissionError(error: any): boolean {
  return !!(error && error.permissionHandled === true);
}

// 403 PERMISSION_REQUIRED:<CODE> means "authenticated but not allowed" (clinic member without the permission,
// or a permission revoked mid-session): warn, refresh the profile so the UI re-gates, and do NOT log out.
export function handlePermissionDenied(error: any, notificationService: AlertNotificationsService, injector: Injector): boolean {
  const requirement = parsePermissionRequired(error);
  if (!requirement) return false;
  notificationService.warning(permissionDeniedMessage(requirement));
  markPermissionHandled(error);
  // Lazy lookup: ProfileService -> HttpClient -> interceptors would be circular at injection time.
  injector.get(ProfileService).getProfile().catch(() => {});
  return true;
}

const PARTNER_NOT_APPROVED_MESSAGE = 'PARTNER_NOT_APPROVED';
const PARTNER_NOT_APPROVED_WARNING =
  'Your clinic is not approved yet. This section will be available once the review is complete.';

// Fix round 1 / I1: deduping for the whole tab session silenced every later failure too (the
// error is still marked handled either way, so a component skipping its own toast got no
// feedback at all past the first one). Instead, re-warn once more than this many ms have passed
// since the last warning - a burst of calls 403ing together (sidemenu, dashboard widgets...)
// still gets one toast, not one per call, but a user who keeps hitting a gated action minutes
// apart keeps getting told why.
const PARTNER_NOT_APPROVED_REWARN_MS = 5000;
let partnerNotApprovedLastWarnedAt = 0;

export function resetPartnerNotApprovedWarning(): void {
  partnerNotApprovedLastWarnedAt = 0;
}

export function isPartnerNotApprovedError(error: any): boolean {
  return error?.status === 403 && error?.error?.message === PARTNER_NOT_APPROVED_MESSAGE;
}

/**
 * Item 3 (new backend contract): partner-panel endpoints 403 with exactly "PARTNER_NOT_APPROVED"
 * when the clinic isn't approved yet. Treated like a permission denial - authenticated but
 * disallowed, so no logout - but UNLIKE handlePermissionDenied it never reloads the profile:
 * the clinic still isn't approved, so every gated call would 403 again and getProfile() would
 * just keep re-firing on every request, looping.
 */
export function handlePartnerNotApproved(
  error: any,
  notificationService: AlertNotificationsService,
  // Injectable for tests, so the 5s window can be exercised deterministically without depending on
  // jasmine's clock/Date mocking; production call sites always use the default (real time).
  now: number = Date.now(),
): boolean {
  if (!isPartnerNotApprovedError(error)) return false;
  if (now - partnerNotApprovedLastWarnedAt > PARTNER_NOT_APPROVED_REWARN_MS) {
    partnerNotApprovedLastWarnedAt = now;
    notificationService.warning(PARTNER_NOT_APPROVED_WARNING);
  }
  markPermissionHandled(error);
  return true;
}

const PARTNER_PENDING_DELETION_MESSAGE = 'PARTNER_PENDING_DELETION';

export function isPartnerPendingDeletionError(error: any): boolean {
  return error?.status === 403 && error?.error?.message === PARTNER_PENDING_DELETION_MESSAGE;
}

/**
 * Account deletion scheduled: the backend locks every panel API except profile and the deletion
 * endpoints with 403 "PARTNER_PENDING_DELETION". The session is valid (owner signed in) and the
 * panel is already redirected to the "deletion scheduled" page by PartnerLockedStatusGuard, so a
 * stray call is swallowed silently: no toast, no logout, no profile reload.
 */
export function handlePartnerPendingDeletion(error: any): boolean {
  if (!isPartnerPendingDeletionError(error)) return false;
  markPermissionHandled(error);
  return true;
}

/**
 * Exported for tests only (fix round 1 / C1): the bug was in this function's RxJS wiring - the
 * "refresh failed" catchError sat downstream of the retry's switchMap, so it also caught (and
 * masked) whatever the retry itself threw, including an already-handled error. Testing this
 * requires exercising the actual pipe, not just the decision functions, hence the export.
 */
export function handleAuthError(
  error: any,
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  notificationService: AlertNotificationsService,
  currentLang: string,
  injector: Injector
): Observable<any> {
  if (handlePermissionDenied(error, notificationService, injector)) {
    return throwError(() => error);
  }
  if (handlePartnerNotApproved(error, notificationService)) {
    return throwError(() => error);
  }
  if (handlePartnerPendingDeletion(error)) {
    return throwError(() => error);
  }

  // Fallback REATTIVO: 401 nonostante il check proattivo (token revocato, clock skew…).
  // Un solo tentativo di refresh + retry; il single-flight in AuthService evita
  // refresh multipli con richieste parallele.
  if (error?.status === 401 && !req.context.get(RETRIED) && authService.isRefreshTokenValid()) {
    // C1 fix: this catchError must sit directly on refreshToken()'s own stream, BEFORE the
    // switchMap - not after it. Placed after (as it originally was), it also catches whatever the
    // retried request's own inner catchError rethrows (including an already-handled, correctly
    // flagged retryError), forces a logout that shouldn't happen, and replaces that retryError
    // with the stale, un-flagged original `error` - so both the flag and the real error were lost.
    // Scoped to refreshToken() alone, it only fires when the token refresh itself fails; a failed
    // retry propagates as-is, exactly as its own catchError below already decided.
    return authService.refreshToken().pipe(
      catchError(() => {
        forceLogout(authService);
        return throwError(() => error);
      }),
      switchMap(token => {
        const retryReq = req.clone({
          headers: req.headers
            .set('Authorization', `Bearer ${token}`)
            .set('Accept-Language', currentLang),
          context: req.context.set(RETRIED, true)
        });
        return next(retryReq).pipe(
          catchError(retryError => normalizeBlobError$(retryError).pipe(
            switchMap(normalizedRetryError => {
              if (handlePermissionDenied(normalizedRetryError, notificationService, injector)) {
                return throwError(() => normalizedRetryError);
              }
              if (handlePartnerNotApproved(normalizedRetryError, notificationService)) {
                return throwError(() => normalizedRetryError);
              }
              if (handlePartnerPendingDeletion(normalizedRetryError)) {
                return throwError(() => normalizedRetryError);
              }
              maybeNotify(normalizedRetryError, req, notificationService);
              if (normalizedRetryError?.status === 401 || normalizedRetryError?.status === 403) {
                forceLogout(authService);
              }
              return throwError(() => normalizedRetryError);
            })
          ))
        );
      })
    );
  }

  // 401 fuori dalle aree protette con una sessione locale non recuperabile (refresh token
  // assente/scaduto): puliamo in silenzio e ritentiamo UNA volta senza Authorization, così la
  // pagina pubblica funziona come per un visitatore anonimo invece di mostrare un toast.
  if (error?.status === 401 && !req.context.get(RETRIED) && !isOnProtectedArea() && authService.isUserLoggedIn()) {
    forceLogout(authService);
    const anonymousReq = req.clone({
      headers: req.headers.delete('Authorization').set('Accept-Language', currentLang),
      context: req.context.set(RETRIED, true)
    });
    return next(anonymousReq).pipe(
      catchError(retryError => {
        maybeNotify(retryError, req, notificationService);
        return throwError(() => retryError);
      })
    );
  }

  maybeNotify(error, req, notificationService);
  if (error?.status === 401 || error?.status === 403) {
    forceLogout(authService);
  }
  return throwError(() => error);
}

function isOnProtectedArea(): boolean {
  return (
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/partners') ||
    location.pathname.startsWith('/support')
  );
}

// Logout + redirect solo dalle aree protette, per non sbattere fuori dalle pagine pubbliche
// (fuori: pulizia silenziosa di token e profilo).
// NB: nessun guard su isUserLoggedIn() — forceLogout è chiamato solo su 401/403 in area
// protetta, dove vogliamo sempre tornare al login, anche (anzi soprattutto) quando il
// token è già assente/scaduto. logout() è idempotente e /login non è area protetta.
function forceLogout(authService: AuthService): void {
  // A fresh session (new login, or a real logout/back-to-login) should be able to warn about
  // PARTNER_NOT_APPROVED again rather than staying silently deduped from a previous session.
  resetPartnerNotApprovedWarning();

  if (isOnProtectedArea()) {
    authService.logout();
  } else {
    // Fuori dalle aree protette: nessun redirect, ma la sessione morta va comunque rimossa,
    // altrimenti ogni pagina pubblica ritenterebbe il refresh (e mostrerebbe errori) all'infinito.
    authService.clearSession();
  }
}

function maybeNotify(
  error: any,
  req: HttpRequest<unknown>,
  notificationService: AlertNotificationsService
): void {
  if (isPublicClinicNotFound(error, req)) {
    // Scheda clinica pubblica inesistente/sospesa/non approvata: la pagina mostra già lo stato
    // "clinica non disponibile", un toast di errore sarebbe solo rumore.
    return;
  }
  if (isComponentHandledError(error)) {
    // Il componente mostra già il messaggio tradotto sul campo (es. numero di una clinica):
    // il toast globale mostrerebbe la chiave i18n grezza.
    return;
  }
  if (!skipErrorNotificationEndpoints.some(prefix => req.url.includes(prefix))) {
    notificationService.error(error);
  }
}

function isPublicClinicNotFound(error: any, req: HttpRequest<unknown>): boolean {
  return req.method === 'GET' && error?.status === 404 && /\/v1\/partners\/\d+(\?|$)/.test(req.url);
}

function isStaticAsset(url: string): boolean {
  return url.includes('/assets/') || url.startsWith('assets/');
}

function isExcludedEndpoint(url: string): boolean {
  return authExcludedEndpoints.some(prefix => url.includes(prefix));
}